import { useMapContext } from '@components/map/MapContext';
import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updateDisplayedPerimeterLinksKeysByMapId } from '@redux/maps/maps.reducer';
import { AUTOMATON_SECTION_FEATURE_TYPES, AUTOMATON_SECTION_LAYER_NAMES } from '@utils/map/map.constants';
import { MapEventOf } from 'mapbox-gl';
import { useCallback } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { FeatureTypeEnum, LayerNameEnum, SensorUniqueKeys } from '@/types/map.types';
import { AutomatonSectionUniqueKeys, SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

export const useDisplayPerimeterLinksEvent = () => {
  const { current: map } = useMap();
  const { mapId, isMeasureActive } = useMapContext();

  const layers: LayerNameEnum[] = [
    LayerNameEnum.PERIMETER_SENSOR_LABELS,
    LayerNameEnum.PERIMETER_SENSOR_ICONS,
    ...AUTOMATON_SECTION_LAYER_NAMES,
  ];

  const updateDisplayedPerimeterLinksKeys = useWithDispatch(updateDisplayedPerimeterLinksKeysByMapId);

  const handleClickAndContextMenu = useCallback(
    (event: MapEventOf<'click' | 'contextmenu'>) => {
      if (!map || event.features === undefined || isMeasureActive) {
        return;
      }

      const feature = event.features[0];
      const featureType = feature.properties?.featureType;

      if ([FeatureTypeEnum.SENSOR_ICON, FeatureTypeEnum.SENSOR_LABEL].includes(featureType)) {
        if (feature.properties?.type === SensorTypeEnum.AUTOMATON) {
          const automatonUniqueKey = (JSON.parse(feature.properties.value) as SensorUniqueKeys).sensorUniqueKey;
          updateDisplayedPerimeterLinksKeys({
            mapId,
            displayedPerimeterLinksKeys: {
              automatonUniqueKey,
            },
          });
        }
        if (feature.properties?.type === SubSensorTypeEnum.AUTOMATON_CABINET) {
          const cabinetUniqueKeys = JSON.parse(feature.properties.value) as SensorUniqueKeys;
          updateDisplayedPerimeterLinksKeys({
            mapId,
            displayedPerimeterLinksKeys: {
              automatonUniqueKey: cabinetUniqueKeys.parentUniqueKey!,
              cabinetUniqueKey: cabinetUniqueKeys.sensorUniqueKey,
            },
          });
        }
      } else if (AUTOMATON_SECTION_FEATURE_TYPES.includes(featureType)) {
        const automatonSectionUniqueKeys = JSON.parse(feature.properties?.value) as AutomatonSectionUniqueKeys | null;
        if (automatonSectionUniqueKeys) {
          updateDisplayedPerimeterLinksKeys({
            mapId,
            displayedPerimeterLinksKeys: automatonSectionUniqueKeys,
          });
        }
      }
    },
    [map, updateDisplayedPerimeterLinksKeys, mapId, isMeasureActive],
  );
  useMapLayerMouseEvent('click', handleClickAndContextMenu, layers);
  useMapLayerMouseEvent('contextmenu', handleClickAndContextMenu, layers);
};
